// CarnivalCard.jsx
import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Button,
} from "@mui/material";
import {
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  Edit as EditIcon,
  Share as ShareIcon,
} from "@mui/icons-material";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import useCarnivalWebSocket from "hooks/useCarnivalWebSocket";
import useUserId from "hooks/useUserId";
import { useTranslation } from "react-i18next";

const CarnivalCard = ({
  carnival,
  onEdit,
  onToggleArchive,
  onSelect,
  onShare,
  showSnackbar,
}) => {
  const navigate = useNavigate();
  const { userId: userIdObject } = useUserId();
  const { t } = useTranslation();

  // Extract user ID properly
  const userId = userIdObject?.userId || userIdObject?.id || userIdObject;

  // Local state for real-time participant count
  const [currentParticipants, setCurrentParticipants] = useState(
    carnival.current_participants || 0
  );

  // WebSocket connection for real-time updates
  const { carnivalData, isConnected } = useCarnivalWebSocket(
    carnival.id,
    userId,
    {
      onParticipantUpdate: (data) => {
        console.log(`Participant update for carnival ${carnival.id}:`, data);
        setCurrentParticipants(data.current_participants);

        // Show notification for participant changes
        if (data.action === "ticket_created") {
          showSnackbar(t("carnivals.newParticipantJoined", { title: carnival.title }), "info");
        } else if (data.action === "interview_completed") {
          showSnackbar(t("carnivals.interviewCompleted", { title: carnival.title }), "success");
        }
      },
      onStatusChange: (data) => {
        console.log(`Status change for carnival ${carnival.id}:`, data);
        if (data.is_archived !== carnival.is_archived) {
          showSnackbar(t("carnivals.caravanStatusUpdated", { title: carnival.title }), "info");
        }
      },
      onError: (error) => {
        console.error(`WebSocket error for carnival ${carnival.id}:`, error);
      },
      debug: process.env.NODE_ENV === "development",
    }
  );

  // Update local state when WebSocket provides new data
  useEffect(() => {
    if (carnivalData && carnivalData.current_participants !== undefined) {
      setCurrentParticipants(carnivalData.current_participants);
    }
  }, [carnivalData]);

  // Fallback to prop value if WebSocket data is not available
  const displayParticipants =
    carnivalData?.current_participants ?? currentParticipants;

  return (
    <Card
      key={carnival.id}
      variant="outlined"
      sx={{
        overflow: "visible",
        borderRadius: 3,
        boxShadow: "0 8px 32px rgba(0, 23, 231, 0.08)",
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        border: "1px solid rgba(0, 23, 231, 0.1)",
        background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0 16px 48px rgba(0, 23, 231, 0.15)",
          borderColor: "rgba(0, 23, 231, 0.2)",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" component="div" color="primary">
              {carnival.title}
            </Typography>
            <Chip
              label={carnival.is_archived ? t("carnivals.archived") : t("carnivals.active")}
              color={carnival.is_archived ? "default" : "success"}
              size="small"
              sx={{
                fontWeight: 500,
                borderRadius: "8px",
                bgcolor: carnival.is_archived ? "#e8eef7" : "#e3f6e8",
                color: carnival.is_archived ? "#5d7b99" : "#2e7d32",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {format(new Date(carnival.created_at), "MMM dd, yyyy")}
            </Typography>
            <Tooltip title={t("carnivals.edit")}>
              <IconButton
                onClick={() => onEdit(carnival)}
                size="small"
                sx={{ color: "primary.main" }}
              >
                <EditIcon fontSize="medium" />
              </IconButton>
            </Tooltip>

            <Tooltip title={carnival.is_archived ? t("carnivals.unarchive") : t("carnivals.archive")}>
              <IconButton
                onClick={() => onToggleArchive(carnival)}
                size="small"
                sx={{
                  color: carnival.is_archived ? "#4caf50" : "#2196f3",
                  "&:hover": {
                    backgroundColor: carnival.is_archived
                      ? "rgba(76, 175, 80, 0.1)"
                      : "rgba(244, 67, 54, 0.1)",
                  },
                }}
              >
                {carnival.is_archived ? (
                  <UnarchiveIcon fontSize="medium" />
                ) : (
                  <ArchiveIcon fontSize="medium" />
                )}
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        <Typography
          variant="body2"
          color="text.secondary"
          paragraph
          sx={{
            p: 2,
            borderRadius: 1,
            bgcolor: "rgba(240, 247, 255, 0.5)",
            mb: 2,
          }}
        >
          {carnival.description}
        </Typography>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {/* <Typography variant="caption" color="text.secondary">
              Start Time:{" "}
              <Typography
                component="span"
                variant="caption"
                fontWeight="medium"
                color="primary.dark"
              >
                {carnival.start_time
                  ? new Date(carnival.start_time)
                      .toISOString()
                      .slice(0, 16)
                      .replace("T", " ")
                  : "N/A"}
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Deadline:{" "}
              <Typography
                component="span"
                variant="caption"
                fontWeight="medium"
                color="primary.dark"
              >
                {new Date(carnival.deadline)
                  .toISOString()
                  .slice(0, 16)
                  .replace("T", " ")}{" "}
              </Typography>
            </Typography> */}
            <Typography variant="caption" color="text.secondary">
              {t("carnivals.startTime")}:{" "}
              <Typography
                component="span"
                variant="caption"
                fontWeight="medium"
                color="primary.dark"
              >
                {carnival.start_time
                  ? format(
                      new Date(carnival.start_time),
                      "MMM dd yyyy 'at' h:mm aa"
                    )
                  : "N/A"}
              </Typography>
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t("carnivals.deadline")}:{" "}
              <Typography
                component="span"
                variant="caption"
                fontWeight="medium"
                color="primary.dark"
              >
                {format(
                  new Date(carnival.deadline),
                  "MMM dd yyyy 'at' h:mm aa"
                )}
              </Typography>
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{
              px: 1.5,
              py: 0.5,
              borderRadius: 1,
              bgcolor: "background.default",
              fontWeight: 500,
              color:
                displayParticipants >= carnival.max_participants
                  ? "error.main"
                  : displayParticipants >= carnival.max_participants * 0.8
                  ? "warning.main"
                  : "info.main",
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            {t("carnivals.participants")}: {displayParticipants}/{carnival.max_participants}
            {isConnected && (
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  bgcolor: "success.main",
                  animation: "pulse 2s infinite",
                }}
                title={t("carnivals.realTimeUpdatesActive")}
              />
            )}
          </Typography>

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => onSelect(carnival)}
              size="small"
              sx={{
                borderRadius: 6,
                px: 2,
                boxShadow: "none",
                borderColor: "#0017E7",
                color: "#0017E7",
                "&:hover": {
                  borderColor: "#0010a3",
                  backgroundColor: "rgba(0, 23, 231, 0.04)",
                  boxShadow: "0 2px 8px rgba(0, 23, 231, 0.2)",
                },
              }}
            >
              {t("carnivals.enterCaravan")}
            </Button>
            {carnival.has_completed_interviews && (
              <Button
                variant="contained"
                size="small"
                sx={{
                  borderRadius: 6,
                  px: 2,
                  boxShadow: "none",
                  background:
                    "linear-gradient(45deg, #0017E7 30%, #1e40af 90%)",
                  "&:hover": {
                    background:
                      "linear-gradient(45deg, #0010a3 30%, #1e3a8a 90%)",
                    boxShadow: "0 2px 8px rgba(0, 23, 231, 0.2)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s ease-in-out",
                }}
                onClick={() => {
                  if (carnival?.id) {
                    console.log("hello1", carnival.id);
                    navigate(`/carnivals/${carnival.id}/results`);
                  } else {
                    showSnackbar(t("carnivals.invalidCaravanId"), "error");
                  }
                }}
              >
                {t("carnivals.viewResults")}
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<ShareIcon />}
              sx={{
                borderRadius: 6,
                px: 2,
                boxShadow: "none",
                background: "linear-gradient(45deg, #1e40af 30%, #3b82f6 90%)",
                "&:hover": {
                  background:
                    "linear-gradient(45deg, #1e3a8a 30%, #2563eb 90%)",
                  boxShadow: "0 2px 8px rgba(30, 64, 175, 0.2)",
                  transform: "translateY(-1px)",
                },
                transition: "all 0.2s ease-in-out",
              }}
              onClick={() => onShare(carnival)}
            >
              {t("carnivals.share")}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CarnivalCard;
